import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';
import type { GridApi } from 'ag-grid-enterprise';
import type { AxiosError } from 'axios';
import { atom, useAtom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router';

import PERMISSIONS from '@/constants/auth.constants';
import { ALERT_RULES_FILTERS_STORAGE_KEY } from '@/constants/common';
import useHandleError from '@/hooks/useHandleError';
import usePermissionsCheck from '@/hooks/usePermissionsCheck';
import { selectedOrganizationAtom } from '@/pages/Organizations';
import { axiosClient } from '@/services/BaseApi.service';

import type {
  AITicketTemplateDTO,
  AlertRuleDetailsDTO,
  AlertRuleDTO,
  AlertRulesFilters,
  AlertRuleUpdateDTO,
  TemplateTicketAICreateOrUpdateDTO
} from './AlertRules.types';

const jotaiSessionStorage = createJSONStorage<AlertRulesFilters>(
  () => sessionStorage
);

export const alertRulesAtom = atom<AlertRuleDTO[]>([]);
export const alertRulesFiltersAtom = atomWithStorage<AlertRulesFilters>(
  ALERT_RULES_FILTERS_STORAGE_KEY,
  {},
  jotaiSessionStorage
);
export const alertRulesGridApiAtom = atom<GridApi | null>(null);

export const isAlertRulesPanelOpenAtom = atom(false);
export const selectedAlertRuleAtom = atom<AlertRuleDetailsDTO | null>(null);
export const selectedAlertRuleIdAtom = atom<string | null>(null);

const applyFilters = (
  items: AlertRuleDTO[],
  filters: AlertRulesFilters,
  organizationId: number
) => {
  if (!Object.keys(filters).length) return items;

  return items.filter((item: AlertRuleDTO) =>
    Object.keys(filters).every((k) =>
      filters[k as keyof AlertRulesFilters]?.some((f: any) => {
        if (k === 'any') {
          return JSON.stringify(Object.values(item))
            .toLocaleLowerCase()
            .includes(f.toLocaleLowerCase());
        }
        if (k === 'tuning') {
          if (organizationId !== 0 && f === 'organization') {
            const orgHasInstructions = item?.organization_instructions?.some(
              (instruction: any) =>
                instruction.organization_id === organizationId &&
                !!instruction.instructions
            );
            return orgHasInstructions;
          }
          if (f === 'organization' && organizationId === 0) {
            const orgInstructionsExist = item?.organization_instructions?.some(
              (instruction: any) => !!instruction.instructions
            );
            return orgInstructionsExist;
          }

          return !!item?.instructions;
        }

        if (k in item) {
          const itemValue = item[k as keyof typeof item];
          if (typeof itemValue === 'string') {
            return itemValue.toLowerCase().includes(f.toLowerCase());
          }
          if (typeof itemValue === 'boolean') {
            return itemValue === (f === 'true');
          }
        }

        return false;
      })
    )
  );
};

export const filteredAlertRulesAtom = atom((get) => {
  const alertRules = get(alertRulesAtom);
  const filters = get(alertRulesFiltersAtom);
  const selectedOrganization = get(selectedOrganizationAtom);

  if (!alertRules.length) {
    return [];
  }

  return applyFilters(alertRules, filters, selectedOrganization.id);
});

export const useFetchAlertRules = () => {
  const [, setAlertRules] = useAtom(alertRulesAtom);
  const handleError = useHandleError();

  const { data, refetch, isSuccess, isLoading, isFetching, isError, error } =
    useQuery<AlertRuleDTO[], AxiosError>({
      queryKey: ['alert_rules/'],
      staleTime: 60 * 1000
    });

  useEffect(() => {
    if (isError) {
      handleError(error as AxiosError, 'Failure to fetch alert rules.');
    }
    if (isSuccess && data) {
      setAlertRules(data);
    }
  }, [isSuccess, data, setAlertRules, error, isError]);

  return { data, isFetching, isLoading, isSuccess, refetch };
};

export const useFetchAlertRuleDetails = () => {
  const [, setSelectedAlertRule] = useAtom(selectedAlertRuleAtom);
  const [selectedAlertRuleId] = useAtom(selectedAlertRuleIdAtom);
  const handleError = useHandleError();

  const { data, refetch, isSuccess, isLoading, isFetching, isError, error } =
    useQuery<AlertRuleDetailsDTO, AxiosError>({
      queryKey: [`alert_rules/${selectedAlertRuleId}`],
      enabled: selectedAlertRuleId !== null
    });

  useEffect(() => {
    if (isError) {
      handleError(error as AxiosError, 'Failure to fetch alert rule details.');
    }
    if (isSuccess && data) {
      setSelectedAlertRule(data);
    }
  }, [
    isSuccess,
    data,
    selectedAlertRuleId,
    setSelectedAlertRule,
    error,
    isError
  ]);

  return { data, isFetching, isLoading, isSuccess, refetch };
};

export const useAlertRuleSelection = () => {
  const [, setSelectedAlertRule] = useAtom(selectedAlertRuleAtom);
  const [, setSelectedAlertRuleId] = useAtom(selectedAlertRuleIdAtom);
  const [searchParams, setSearchParams] = useSearchParams();
  const [gridApi] = useAtom(alertRulesGridApiAtom);

  const deselectAlertRule = useCallback(() => {
    const updatedParams = new URLSearchParams(searchParams);
    updatedParams.delete('alertRule');
    setSearchParams(updatedParams);
    setSelectedAlertRule(null);
    setSelectedAlertRuleId(null);
    gridApi?.deselectAll();
  }, [gridApi, searchParams]);

  const selectAlertRule = useCallback(
    (id: string) => {
      const updatedParams = new URLSearchParams(searchParams.toString());
      updatedParams.set('alertRule', id);
      setSearchParams(updatedParams);
    },
    [searchParams]
  );

  return {
    deselectAlertRule,
    selectAlertRule
  };
};

export const useUpdateAlertRule = (handleClose: () => void) => {
  const handleError = useHandleError();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedAlertRule] = useAtom(selectedAlertRuleAtom);
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);

  const queryString =
    selectedOrganization.id > 0
      ? `/alert_rules/${selectedAlertRule?.trigger_id}?organization_label=${selectedOrganization.label}`
      : `/alert_rules/${selectedAlertRule?.trigger_id}`;

  const mutation = useMutation({
    mutationFn: (updatedAlertRule: AlertRuleUpdateDTO) => {
      return axiosClient.put(queryString, updatedAlertRule);
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to update alert rule.');
    },
    onSuccess: () => {
      enqueueSnackbar(`Alert Rule Updated`, {
        variant: 'success'
      });
      queryClient.invalidateQueries({
        queryKey: ['alert_rules/'] // Invalidate both the alert rule details and list queries
      });
      handleClose();
      queryClient.invalidateQueries({
        queryKey: [`alert_rules/${selectedAlertRule?.trigger_id}`]
      });
    }
  });

  return {
    updateAlertRule: mutation.mutate,
    isLoadingUpdateAlertRule: mutation.isPending
  };
};

export const useFetchAITicketTemplates = () => {
  const handleError = useHandleError();
  const hasAiPermission = usePermissionsCheck([
    PERMISSIONS.CASES_UPDATE,
    PERMISSIONS.INTERNAL
  ]);

  const { data, isLoading, isFetching, isError, error } = useQuery<
    AITicketTemplateDTO[],
    AxiosError
  >({
    queryKey: ['templates_ticket_ai/'],
    staleTime: 60 * 1000,
    enabled: hasAiPermission
  });

  useEffect(() => {
    if (isError) {
      handleError(error as AxiosError, 'Failure to fetch ai ticket templates.');
    }
  }, [isError, error]);

  return { data, isFetching, isLoading };
};

const ticketTemplateAiSuccess = (
  queryClient: QueryClient,
  ruleName: string | null
) => {
  queryClient.invalidateQueries({
    queryKey: ['templates_ticket_ai/']
  });
  queryClient.invalidateQueries({
    queryKey: [`alert_rules/${ruleName}`]
  });
};

export const useCreateAITicketTemplates = () => {
  const handleError = useHandleError();
  const [selectedAlertRuleId] = useAtom(selectedAlertRuleIdAtom);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (createInfo: { data: TemplateTicketAICreateOrUpdateDTO }) => {
      return axiosClient.post(`/templates_ticket_ai/`, createInfo.data);
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to create template.');
    },
    onSuccess: () => {
      ticketTemplateAiSuccess(queryClient, selectedAlertRuleId);
    }
  });
};

export const useUpdateAiTicketTemplate = () => {
  const handleError = useHandleError();
  const [selectedAlertRuleId] = useAtom(selectedAlertRuleIdAtom);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (editTemplateInfo: {
      data: TemplateTicketAICreateOrUpdateDTO;
      id: number;
    }) => {
      const updatedInfo = editTemplateInfo.data;
      const { id } = editTemplateInfo;

      return axiosClient.put(`/templates_ticket_ai/${id}`, updatedInfo);
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to update template.');
    },
    onSuccess: () => {
      ticketTemplateAiSuccess(queryClient, selectedAlertRuleId);
    }
  });
};

export const useDeleteAiTicketTemplate = () => {
  const handleError = useHandleError();
  const [selectedAlertRuleId] = useAtom(selectedAlertRuleIdAtom);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (deleteInfo: { id: number }) => {
      return axiosClient.delete(`/templates_ticket_ai/${deleteInfo.id}`);
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to delete template.');
    },
    onSuccess: () => {
      ticketTemplateAiSuccess(queryClient, selectedAlertRuleId);
    }
  });
};
